import { BATTLE_MODE_COPY, defaultAccountCopy } from "@loot-os/api-contract";
import type { EndUser } from "@loot-os/client";
import type {
  AccountHistoryContent,
  BattleHistoryRow,
  HistoryState,
  OpenHistoryRow,
} from "@loot-os/design-system/templates/account/accountContent";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { battleOutcome, viewerSeat } from "../../../../lib/battleOutcome.js";
import { useCurrency } from "../../../../lib/currency.js";
import {
  boxesQueryOptions,
  myBattlesInfiniteQueryOptions,
  opensInfiniteQueryOptions,
} from "../../../../lib/queries.js";
import { useShopper } from "../../../../lib/shopper.js";
import { useTenant } from "../../../../lib/tenant.js";
import { useTerminology } from "../../../../lib/terminology.js";

/** How many of a battle's boxes a history row draws before "+N". */
const ROUNDS_SHOWN = 4;

/**
 * History: GET /v1/me/opens, each linked to its verification, and GET /v1/me/battles where the
 * store runs battles. Battle opens are not in /v1/me/opens (they roll against the battle's salt
 * and nonce), so this is where they reach the account. Fetched only while History shows.
 */
export function useHistoryContent({
  me,
  active,
}: {
  me: EndUser | null;
  active: boolean;
}): AccountHistoryContent {
  const { sessionToken } = useShopper();
  const { hasFeature } = useTenant();
  const currency = useCurrency();
  const terms = useTerminology();
  const copy = defaultAccountCopy(terms).history;

  const opensQuery = useInfiniteQuery({
    ...opensInfiniteQueryOptions(),
    enabled: active && !!sessionToken,
  });
  const opens = opensQuery.data?.pages.flatMap((page) => page.data) ?? [];

  // Battles the shopper holds a seat in, in every state: a lobby they created or joined, one
  // running, and finished ones.
  const showBattles = hasFeature("battles");
  const battlesQuery = useInfiniteQuery({
    ...myBattlesInfiniteQueryOptions(),
    enabled: active && showBattles && !!sessionToken,
  });
  const battles = battlesQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const boxes = useQuery({ ...boxesQueryOptions(), enabled: showBattles && battles.length > 0 });
  const boxMap = useMemo(
    () => new Map((boxes.data ?? []).map((b) => [b.id, { name: b.name, imageUrl: b.imageUrl }])),
    [boxes.data],
  );

  const openRows: OpenHistoryRow[] = opens.map((open) => ({
    id: open.id,
    item: { name: open.item.name, imageUrl: open.item.imageUrl },
    valueCents: open.item.value,
    costCents: open.costCents,
    when: new Date(open.createdAt).toLocaleString(),
    verify: {
      label: copy.opens.verify,
      href: `/fairness?openId=${encodeURIComponent(open.id)}`,
      emphasis: "secondary",
    },
  }));

  const battleRows: BattleHistoryRow[] = battles.map((b) => {
    const outcome = battleOutcome(b, viewerSeat(b, me?.id));
    return {
      id: b.id,
      rounds: b.rounds.slice(0, ROUNDS_SHOWN).map((round) => ({
        name: boxMap.get(round.boxId)?.name ?? terms.box.singular,
        imageUrl: boxMap.get(round.boxId)?.imageUrl ?? "",
      })),
      moreRounds: Math.max(0, b.rounds.length - ROUNDS_SHOWN),
      mode: {
        label: BATTLE_MODE_COPY[b.mode].label,
        rule: BATTLE_MODE_COPY[b.mode].rule,
        tone: b.mode === "crazy" ? "danger" : "neutral",
      },
      summary: copy.battles.summary(b.roundCount, b.playerCount),
      result: {
        label: copy.battles.outcome[outcome],
        tone: outcome === "won" ? "success" : outcome === "lost" ? "neutral" : "accent",
      },
      // Σ every seat's pulls; null until the battle completed.
      potCents:
        b.status === "completed"
          ? b.seats.reduce((sum, s) => sum + (s.totalValueCents ?? 0), 0)
          : null,
      costCents: b.priceCents,
      when: new Date(b.createdAt).toLocaleString(),
      link: {
        label: copy.battles.link[outcome],
        href: `/battles/${encodeURIComponent(b.id)}`,
        emphasis: "secondary",
      },
    };
  });

  return {
    title: copy.title,
    currency,
    viewLabel: copy.viewLabel,
    opens: {
      label: copy.opens.label,
      columns: copy.opens.columns,
      state: listState({
        rows: openRows,
        pending: opensQuery.isPending,
        error: opensQuery.error,
        errorFallback: "failed to load opens",
        empty: {
          message: copy.opens.empty,
          action: { label: copy.opens.browse, href: "/boxes", emphasis: "primary" },
        },
      }),
      paging: {
        hasMore: opensQuery.hasNextPage,
        loading: opensQuery.isFetchingNextPage,
        onLoadMore: () => void opensQuery.fetchNextPage(),
        label: copy.paging.loadMore,
        status: copy.paging.showing,
      },
    },
    battles: showBattles
      ? {
          label: copy.battles.label,
          columns: copy.battles.columns,
          pendingPot: copy.battles.pendingPot,
          state: listState({
            rows: battleRows,
            pending: battlesQuery.isPending,
            error: battlesQuery.error,
            errorFallback: `failed to load ${terms.battle.plural}`,
            empty: {
              message: copy.battles.empty,
              action: { label: copy.battles.start, href: "/battles", emphasis: "primary" },
            },
          }),
          paging: {
            hasMore: battlesQuery.hasNextPage,
            loading: battlesQuery.isFetchingNextPage,
            onLoadMore: () => void battlesQuery.fetchNextPage(),
            label: copy.paging.loadMore,
            status: copy.paging.showing,
          },
        }
      : null,
    footnote: showBattles ? copy.footnote.withBattles : copy.footnote.opensOnly,
  };
}

/**
 * A list's state. A failure is shown in the list's place only while nothing loaded; once rows
 * are there, a later page that failed leaves them (the Load more button stays to retry).
 */
function listState<Row>(args: {
  rows: Row[];
  pending: boolean;
  error: Error | null;
  errorFallback: string;
  empty: { message: string; action: { label: string; href: string; emphasis: "primary" } };
}): HistoryState<Row> {
  if (args.rows.length > 0) return { status: "ready", rows: args.rows };
  if (args.error) return { status: "error", message: args.error.message || args.errorFallback };
  if (args.pending) return { status: "loading" };
  return { status: "empty", message: args.empty.message, action: args.empty.action };
}
